import * as mobx from 'mobx';
import { action, observable } from 'mobx';
import { EventBlueprintAction, EventBlueprintField, IEventBlueprintDTO } from '../../../../api/blueprints/event.blueprint.types';
import * as IoC from 'typescript-ioc';
import { IBlueprintsDAO, IMeetingTemplateDTO } from '../../../../api/blueprints/blueprints.dao';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { ActionType } from '../../../../api/blueprints/blueprint.types';
import { TObject } from '../../../../infrastructure/type-extensions';

type BlueprintFieldsValues = {
    [key in EventBlueprintField['name']]: EventBlueprintField['value'];
};

export class EventFormBlueprintsVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @observable
    private blueprints: TObject<IEventBlueprintDTO> = {};
    @observable
    private id: string | null;
    @observable
    private loading: boolean = false;
    private meetingTemplates: IMeetingTemplateDTO[] = [];

    constructor(
        blueprintId: string | null,
        private readonly accountId: number,
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
    ) {
        this.id = blueprintId;
    }

    @mobx.action
    public async initialize(): Promise<void> {
        this.startLoading();
        try {
            const params = { accountId: this.accountId };

            const [ blueprintsResponse, templatesResponse ] = await Promise.all([
                this.blueprintsDAO.getEventBlueprintList(params),
                this.blueprintsDAO.getMeetingTemplateList(params)
            ]);

            this.setEventBlueprints(blueprintsResponse);
            this.meetingTemplates = templatesResponse;
        } catch (error) {
            EventFormBlueprintsVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    @action
    public setBlueprintId(id: string): void {
        this.id = id || null;
    }

    public get blueprintId(): string | null {
        return this.id;
    }

    public getBlueprintName(): string {
        return this.blueprints[this.id]?.name ?? '';
    }

    public getSelectedBlueprint(): IEventBlueprintDTO | null {
        return this.blueprints[this.id] ?? null;
    }

    public getBlueprintFieldsValues(): BlueprintFieldsValues {
        return this.getBlueprintFields()
            .reduce((fieldsValues, { name, value }) => {
                fieldsValues[name] = value;
                return fieldsValues;
            }, {} as BlueprintFieldsValues)
        ;
    }

    public getBlueprintList(): IEventBlueprintDTO[] {
        return Object.values(this.blueprints);
    }

    public getMeetingTemplateList(): IMeetingTemplateDTO[] {
        return this.meetingTemplates;
    }

    public blueprintHasPassportSiteAction(): boolean {
        return this.getBlueprintActions().some(act => act.type === ActionType.PASSPORT_SITE);
    }

    public blueprintHasSingleMeetingAction(): boolean {
        return this.getBlueprintActions().some(act => act.type === ActionType.SINGLE_MEETING);
    }

    private getBlueprintFields(): EventBlueprintField[] {
        return this.blueprints[this.id]?.fields ?? [];
    }

    private getBlueprintActions(): EventBlueprintAction[] {
        return this.blueprints[this.id]?.actions ?? [];
    }

    @mobx.action
    private setEventBlueprints(eventBlueprints: IEventBlueprintDTO[]): void {
        const _blueprints = {};

        eventBlueprints
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach(blueprint => {
                _blueprints[blueprint.blueprintId] = blueprint;
            })
        ;

        this.blueprints = _blueprints;
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    @action
    private startLoading(): void {
        this.loading = true;
    }
    @action
    private stopLoading(): void {
        this.loading = false;
    }
}
